import React, { useState } from 'react';
import { Table, Switch, Input, Button, message, Row, Col } from 'antd';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import merchantAPI from 'services/merchants';

function Webhook({ data }) {
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const webhookConfig = [
    {
      key: 'transaction',
      description: 'Transaction Webhook',
      urlKey: 'webhook_transaction_url',
      statusKey: 'webhook_transaction_status'
    },
    {
      key: 'refund',
      description: 'Refund Webhook',
      urlKey: 'webhook_refund_url',
      statusKey: 'webhook_refund_status'
    },
    {
      key: 'settlement',
      description: 'Settlement Webhook',
      urlKey: 'webhook_payout_url',
      statusKey: 'webhook_payout_status'
    },
    {
      key: 'submerchant',
      description:
        'Submerchant Webhook Call: If enabled, You will get an API call from our server to your server for every successful submerchant activation.',
      urlKey: 'webhook_kyc_url',
      statusKey: 'webhook_kyc_status'
    },
    {
      key: 'auto_debit',
      description: 'Auto Debit Webhook',
      urlKey: 'webhook_auto_debit_url',
      statusKey: 'webhook_auto_debit_status'
    }
  ];

  const [formData, setFormData] = useState(() =>
    webhookConfig.map((item) => ({
      key: item.key,
      description: item.description,
      urlKey: item.urlKey,
      statusKey: item.statusKey,
      url: data?.[item.urlKey] || '',
      initialUrl: data?.[item.urlKey] || '', // Track initial URL
      enabled: !!data?.[item.statusKey]
    }))
  );

  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const isValidUrl = (url) => {
    const regex =
      /^https:\/\/[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/;
    return regex.test(url);
  };

  const handleUrlChange = (key, value) => {
    setFormData((prev) => prev.map((item) => (item.key === key ? { ...item, url: value } : item)));

    setErrors((prev) => ({
      ...prev,
      [key]: value && !isValidUrl(value) ? 'Please enter a valid URL' : ''
    }));
  };

  const handleSwitchChange = (key, value) => {
    setFormData((prev) =>
      prev.map((item) => {
        if (item.key === key) {
          return { ...item, enabled: value };
        }
        return item;
      })
    );
  };

  const handleSave = async () => {
    setLoading(true);
    const newErrors = {};
    let hasError = false;

    formData.forEach((item) => {
      const urlEmpty = !item.url || item.url.length === 0;

      if (item.initialUrl?.length > 0 && urlEmpty) {
        newErrors[item.key] = 'URL cannot be empty';
        hasError = true;
        return; // skip further checks
      }

      if (item.enabled && urlEmpty) {
        newErrors[item.key] = 'Please enter a valid URL';
        hasError = true;
        return;
      }

      if (!urlEmpty && !isValidUrl(item.url)) {
        newErrors[item.key] = 'Please enter a valid URL';
        hasError = true;
      }
    });

    if (hasError) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    const payload = formData.reduce((acc, item) => {
      if (item.url?.length === 0) {
        acc[item.urlKey] = null;
      } else acc[item.urlKey] = item.url;
      acc[item.statusKey] = item.enabled;
      return acc;
    }, {});

    try {
      const response = await merchantAPI.updateMerchantSettingData({
        merchant_id: parseInt(params?.id, 10),
        webhook_settings: payload
      });
      if (response?.success) {
        message.success('Webhooks updated successfully!');
        setIsEditing(false);
        setErrors({});
      } else {
        message.error('Failed to update webhooks.');
      }
    } catch (error) {
      message.error('Failed to update webhooks.');
    }
    setLoading(false);
  };

  const columns = [
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'URL with your registered domain',
      dataIndex: 'url',
      key: 'url',
      render: (_, record) => (
        <div>
          <Input
            value={record.url}
            onChange={(e) => handleUrlChange(record.key, e.target.value)}
            disabled={!isEditing || !record.enabled} // <-- disable if not editing or not enabled
            placeholder="https://example.com/webhook"
            style={{
              width: '100%',
              color: '#000',
              borderColor: errors[record.key] ? 'red' : undefined
            }}
          />
          {errors[record.key] && (
            <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{errors[record.key]}</div>
          )}
        </div>
      )
    },
    {
      title: 'Enable',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (_, record) => (
        <Switch
          checked={record.enabled}
          onChange={(checked) => handleSwitchChange(record.key, checked)}
          disabled={!isEditing}
        />
      )
    }
  ];

  return (
    <div>
      <Table columns={columns} dataSource={formData} pagination={false} rowKey="key" />
      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Button
            loading={loading}
            type="primary"
            onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
          >
            {isEditing ? 'Save' : 'Update'}
          </Button>
        </Col>
      </Row>
    </div>
  );
}

Webhook.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired
};

export default Webhook;
